import { cn } from '@mdvip/shared/lib/utils';
import * as React from 'react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { InfoDot } from './InfoDot';

export type FloatFieldOption = string | { value: string; label: string };

function optionValue(option: FloatFieldOption): string {
  return typeof option === 'string' ? option : option.value;
}

/**
 * Base UI's `Select.Value` has no built-in value→label lookup — without a `children` render
 * function it displays the raw stored value verbatim, not the matching option's label. Only
 * matters when a value differs from its label (e.g. an enum member vs. its display text); options
 * where they're identical (a state abbreviation, say) never exposed this.
 */
function getSelectOptionLabel(options: FloatFieldOption[], value: string | null): string | null {
  if (value == null) return null;
  const match = options.find((option) => optionValue(option) === value);
  if (!match) return value;
  return typeof match === 'string' ? match : match.label;
}

/**
 * Info hint content. A plain string renders as both the tooltip's accessible label and its
 * content (the common case). The object form is for rich content (e.g. a diagram) that needs its
 * own accessible `label` distinct from the (decorative) visual `content`, and optionally a wider
 * tooltip box than the default `max-w-48` via `contentClassName`.
 */
export type FloatFieldInfo = string | { label: string; content: React.ReactNode; contentClassName?: string };

export interface FloatFieldProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'prefix'> {
  /** Control rendered inside the box. @default 'text' */
  kind?: 'text' | 'select' | 'date' | 'textarea';
  /** Floating label text. */
  label: string;
  /** Controlled value. */
  value?: string;
  /** Change handler — receives an event-like `{ target: { value } }` for both input and select. */
  onChange?: (event: { target: { value: string } }) => void;
  /** Options for `kind="select"` — strings or `{ value, label }`. */
  options?: FloatFieldOption[];
  /** Static prefix shown before the value once floated (e.g. "$"). */
  prefix?: string;
  /** Info hint — renders an `InfoDot` tooltip (text fields only). */
  info?: FloatFieldInfo;
  /** Error state — delegates to the control's `aria-invalid` styling + destructive label. @default false */
  error?: boolean;
  /** Helper / error text under the box. */
  hint?: React.ReactNode;
  /** @default false */
  disabled?: boolean;
  /**
   * Extra props spread onto the underlying control (`type`, `inputMode`, `maxLength`, `rows`, …).
   * A `placeholder` shows only while the field is focused and empty, so a format hint never sits
   * underneath the label at rest.
   */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> &
    Pick<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows'> & { className?: string };
  /** md = 48px, lg = 56px. Ignored by `kind="textarea"`, whose height comes from `rows`. @default 'md' */
  size?: 'md' | 'lg';
  /** Associates the floating `<label>` with the control. Always required for a11y. */
  id: string;
}

/**
 * FloatField — boxed field with a floating label (text / select / date / textarea).
 *
 * A thin composition layer over the shared `Input` / `Select` / `Textarea`: it adds a relative
 * wrapper + a floating `<label>` and standardizes the height to 48px (md) / 56px (lg). It does NOT
 * re-implement the field — the control is the real primitive, so its focus ring, invalid state
 * and tokens are inherited. Float state is derived from focus + value; error styling is
 * delegated to `aria-invalid`. Self-labeling — don't wrap it in a `FieldLabel`.
 *
 * `kind="textarea"` is the multi-line variant: the label parks top-left rather than centered, and
 * the resting height comes from `inputProps.rows` instead of `size`.
 *
 * Forwards its ref to the underlying control (`kind="select"` never forwarded one, matching the old
 * `FormSelectField`'s behavior) so `FormFloatField` can register the DOM node with react-hook-form
 * for `shouldFocusError`.
 */
export const FloatField = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, FloatFieldProps>(function FloatField(
  {
    kind = 'text',
    label,
    value = '',
    onChange,
    options = [],
    prefix,
    info,
    error = false,
    hint,
    disabled = false,
    inputProps = {},
    size = 'md',
    id,
    className,
    ...rest
  },
  ref,
) {
  const [focused, setFocused] = React.useState(false);
  const h = size === 'lg' ? 'h-14' : 'h-12';
  const padding = size === 'lg' ? 'pt-[1.4rem] pb-[0.15rem]' : 'pt-[1.15rem] pb-[0.15rem]';
  // A non-blank value always floats, same as a text field — regardless of whether it matches a
  // known option (a stored value the dropdown no longer offers still renders via
  // `getSelectOptionLabel`'s raw-value fallback, so it must float too). A blank value only floats
  // when a real option is explicitly bound to it (e.g. a "None" choice a member can pick to clear
  // a field) — otherwise its rendered text would overlap the unfloated label in the same spot.
  const hasValue =
    kind === 'select'
      ? value != null && (value !== '' || options.some((option) => optionValue(option) === ''))
      : value !== undefined && value !== null && value.length > 0;
  // kind="date" always shows the native date control, unless a caller explicitly overrides it.
  const type = inputProps.type ?? (kind === 'date' ? 'date' : 'text');
  const floated = focused || hasValue || kind === 'date';
  // A format hint (e.g. "(000) 000-0000") would sit underneath the unfloated label at rest, so it
  // only surfaces once the member is in the field with nothing typed yet.
  const showPlaceholder = focused && !hasValue;

  // A textarea's label parks at the top of the box in both states; a single-line field's is
  // vertically centered until it floats.
  //
  // The textarea's label also has to OCCLUDE its content, not just sit above it: `padding-top`
  // offsets where the text starts but reserves nothing, so once the content is taller than the box
  // every scrolled line passes straight under the label. So the label spans the full width and
  // carries the control's own background, insetting 1px to stay inside the border. The background
  // has to track the control's three states or the band shows as a patch.
  const labelPositionClassName =
    kind === 'textarea'
      ? cn(
          'inset-x-px top-px rounded-t-md px-3 pb-1',
          error
            ? 'bg-[color-mix(in_oklab,var(--destructive)_14%,var(--card))]'
            : disabled
              ? 'bg-[color-mix(in_oklab,var(--foreground)_8%,var(--background))]'
              : 'bg-card',
          floated ? 'translate-y-0 pt-2 text-xs' : 'translate-y-0 pt-[0.8rem] text-sm',
        )
      : cn(
          'left-3',
          floated
            ? cn('translate-y-0 text-xs', size === 'lg' ? 'top-2' : 'top-1.5')
            : 'top-1/2 -translate-y-1/2 text-sm',
        );

  const labelClassName = cn(
    'pointer-events-none absolute z-10 whitespace-nowrap transition-all duration-150',
    error ? 'text-destructive-foreground' : focused ? 'text-foreground' : 'text-muted-foreground',
    labelPositionClassName,
  );

  // The hint doubles as the validation message, so it's tied to the control: without this a screen
  // reader announces `aria-invalid` with no reason attached.
  const hintId = hint ? `${id}-hint` : undefined;
  const hintClassName = cn('mt-1.5 text-xs', error ? 'text-destructive-foreground' : 'text-muted-foreground');

  if (kind === 'select') {
    return (
      <div data-slot="float-field" className={cn('min-w-0', className)} {...rest}>
        <div className="relative">
          <Select
            value={value}
            disabled={disabled}
            onValueChange={(val: string | null) => onChange?.({ target: { value: val ?? '' } })}
          >
            <SelectTrigger
              id={id}
              size="none"
              aria-invalid={error || undefined}
              aria-describedby={hintId}
              className={cn('w-full', h)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
            >
              <SelectValue
                placeholder=""
                className={cn(
                  'text-sm text-foreground',
                  floated && (size === 'lg' ? 'translate-y-2' : 'translate-y-[7px]'),
                )}
              >
                {(val: string | null) => getSelectOptionLabel(options, val)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => {
                const itemValue = optionValue(option);
                const itemLabel = typeof option === 'string' ? option : option.label;
                return (
                  <SelectItem key={itemValue} value={itemValue}>
                    {itemLabel}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <label htmlFor={id} className={labelClassName}>
            {label}
          </label>
        </div>
        {hint && (
          <p id={hintId} role={error ? 'alert' : undefined} className={hintClassName}>
            {hint}
          </p>
        )}
      </div>
    );
  }

  const { className: inputClassName, placeholder, ...restInputProps } = inputProps;

  if (kind === 'textarea') {
    const { rows, ...restTextareaProps } = restInputProps;
    return (
      <div data-slot="float-field" className={cn('min-w-0', className)} {...rest}>
        <div className="relative">
          <Textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            id={id}
            rows={rows}
            value={value}
            disabled={disabled}
            placeholder={showPlaceholder ? (placeholder ?? ' ') : ' '}
            aria-invalid={error || undefined}
            aria-describedby={hintId}
            onChange={onChange as React.ChangeEventHandler<HTMLTextAreaElement>}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            className={cn('w-full pt-[1.6rem] pb-[0.6rem]', inputClassName)}
            {...(restTextareaProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          />
          <label htmlFor={id} className={labelClassName}>
            {label}
          </label>
        </div>
        {hint && (
          <p id={hintId} role={error ? 'alert' : undefined} className={hintClassName}>
            {hint}
          </p>
        )}
      </div>
    );
  }

  return (
    <div data-slot="float-field" className={cn('min-w-0', className)} {...rest}>
      <div className="relative">
        <Input
          ref={ref as React.Ref<HTMLInputElement>}
          id={id}
          type={type}
          value={value}
          disabled={disabled}
          placeholder={showPlaceholder ? (placeholder ?? ' ') : ' '}
          aria-invalid={error || undefined}
          aria-describedby={hintId}
          onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={cn('w-full', h, padding, prefix && 'pl-6', info && 'pr-9', inputClassName)}
          {...restInputProps}
        />
        {prefix && (
          <span
            className={cn(
              // Overlays the input's own content box (same height, same asymmetric padding that
              // reserves room for the floated label) and centres the glyph in it, so the prefix
              // shares the value's baseline instead of relying on a hand-tuned offset. Inherits the
              // Input's responsive type scale for the same reason.
              'pointer-events-none absolute inset-y-0 left-3 flex items-center text-base md:text-sm',
              padding,
              floated ? 'text-foreground' : 'text-transparent',
            )}
          >
            {prefix}
          </span>
        )}
        {info &&
          (typeof info === 'string' ? (
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              <InfoDot text={info} />
            </span>
          ) : (
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              <InfoDot text={info.label} contentClassName={info.contentClassName}>
                {info.content}
              </InfoDot>
            </span>
          ))}
        <label htmlFor={id} className={labelClassName}>
          {label}
        </label>
      </div>
      {hint && (
        <p id={hintId} role={error ? 'alert' : undefined} className={hintClassName}>
          {hint}
        </p>
      )}
    </div>
  );
});
